import * as t from 'io-ts';
import { DateFromUnixTime } from 'io-ts-types/lib/DateFromUnixTime';
import { id, links, meta } from '@/services/api';
import { IssueActivities, InspectionActivities, ChecklistActivities } from '@/entities/activity/constants';
import { IssueType } from '@/entities/issue';
import { withFallback } from 'io-ts-types/lib/withFallback';

const baseUser = t.type({
  id,
  name: t.string,
});

const user = t.intersection([
  baseUser,
  t.type({
    data: t.type({
      name: t.string,
    }),
  }),
]);

const sensor = t.type({
  id,
  data: t.type({
    title: t.string,
  }),
});

export const baseActivity = t.type({
  type: t.union([
    t.literal('log'),
    t.literal('comment'),
    t.literal('approval'),
  ]),
  field: withFallback(t.string, ''),
  activity: t.union([
    t.literal(IssueActivities.CommentAdded),
    t.literal(IssueActivities.Created),
    t.literal(IssueActivities.FieldUpdated),
    t.literal(IssueActivities.FieldRemoved),
    t.literal(IssueActivities.ManualFormCreated),
    t.literal(IssueActivities.AutomaticFormCreated),
    t.literal(IssueActivities.ApprovalDeleted),
    t.literal(IssueActivities.ApprovalReset),
    t.literal(IssueActivities.ApprovalRejected),
    t.literal(IssueActivities.ApprovalApproved),
    t.literal(InspectionActivities.ApprovalChangesRequested),
    t.literal(InspectionActivities.Started),
    t.literal(InspectionActivities.Submitted),
    t.literal(InspectionActivities.Edited),
    t.literal(InspectionActivities.EditedSubmitted),
    t.literal(InspectionActivities.Available),
    t.literal(InspectionActivities.Overdue),
    t.literal(InspectionActivities.ApprovalDeleted),
    t.literal(InspectionActivities.ApprovalReset),
    t.literal(InspectionActivities.ApprovalRejected),
    t.literal(InspectionActivities.ApprovalApproved),
    t.literal(InspectionActivities.ApprovalChangesRequested),
    t.literal(ChecklistActivities.TemplateCreated),
    t.literal(ChecklistActivities.TemplateUpdated),
    t.literal(ChecklistActivities.TemplateCreatedActive),
    t.literal(ChecklistActivities.TemplateCreatedInactive),
    t.literal(ChecklistActivities.TemplateUpdatedActive),
    t.literal(ChecklistActivities.TemplateUpdatedInactive),
  ]),
  user: t.union([user, t.null]),
  sensor: t.union([sensor, t.null, t.undefined]),
  createdAt: DateFromUnixTime,
});

// Inspection
export const inspectionActivityCodec = t.intersection([
  baseActivity,
  t.type({
    payload: t.record(t.string, t.unknown),
  }),
]);

// Issue
export const payloadContent = t.type({
  id: t.union([t.null, t.string, t.number]),
  value: t.union([t.null, t.string, t.number]),
});

export const activityPayload = t.type({
  old: t.union([payloadContent, t.array(payloadContent)]),
  new: t.union([payloadContent, t.array(payloadContent)]),
});

export const createdCodec = t.intersection([
  baseActivity,
  t.type({
    payload: t.type({
      type: t.union([
        t.literal(IssueType.Automatic),
        t.literal(IssueType.InForm),
        t.literal(IssueType.Sensor),
        t.literal(IssueType.AdHoc),
        t.literal(IssueType.ManualForm),
        t.literal(IssueType.AutomaticForm),
      ]),
      inspectionId: t.union([t.number, t.null]),
    }),
  }),
]);

export const commentCodec = t.intersection([
  baseActivity,
  t.type({
    payload: t.type({
      comment: t.string,
    }),
  }),
]);

export const fieldUpdatedCodec = t.intersection([
  baseActivity,
  t.type({
    field: t.string,
    payload: t.union([t.null, activityPayload]),
  }),
]);

export const fieldRemovedCodec = t.intersection([
  baseActivity,
  t.type({
    field: t.string,
    payload: t.union([t.null, activityPayload]),
  }),
]);

export const manualFormCreated = t.intersection([
  baseActivity,
  t.type({
    payload: t.type({
      sensorId: t.union([t.number, t.null]),
      questionId: t.union([t.number, t.null]),
      inspectionId: t.union([t.number, t.null]),
    }),
  }),
]);

export const automaticFormCreated = manualFormCreated;

export const dataCommentCodec = t.type({
  data: commentCodec,
});

// codecs

export const recordCodec = t.union([
  baseActivity,
  fieldUpdatedCodec,
  fieldRemovedCodec,
  createdCodec,
  commentCodec,
  inspectionActivityCodec,
  manualFormCreated,
  automaticFormCreated,
]);

export const listCodec = t.type({
  data: t.array(recordCodec),
  meta,
  links,
});
